import { PracticeTypeData } from "@/utils/practiceTypeData";
import { CheckCircle2, AlertCircle, Info } from "lucide-react";
import { InteractiveCard } from "@/components/ui/interactive-card";
import { ScrollAnimatedSection } from "@/components/shared/ScrollAnimatedSection";

interface RecommendedCoverageProps {
  practiceType: PracticeTypeData;
}

const RecommendedCoverage = ({ practiceType }: RecommendedCoverageProps) => {
  const getImportanceIcon = (importance: string) => {
    switch (importance) {
      case "essential":
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case "recommended":
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case "optional":
        return <Info className="w-5 h-5 text-blue-500" />;
      default:
        return null;
    }
  };

  const getImportanceLabel = (importance: string) => {
    switch (importance) {
      case "essential":
        return "Unverzichtbar";
      case "recommended":
        return "Empfohlen";
      case "optional":
        return "Optional";
      default:
        return "";
    }
  };

  return (
    <section className="py-12 sm:py-16 md:py-20 bg-muted/30">
      <ScrollAnimatedSection>
        <div className="container mx-auto max-w-6xl px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
              Empfohlene Versicherungsleistungen
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground max-w-3xl mx-auto">
              Unsere Empfehlungen für optimalen Schutz Ihrer {practiceType.name.toLowerCase()}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {practiceType.recommendedCoverage.map((coverage, index) => (
              <InteractiveCard
                key={index}
                enableTilt={true}
                enableDynamicShadow={true}
                className="p-6 border-border/50 hover:border-accent/50"
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-lg font-semibold">{coverage.coverage}</h3>
                  <div className="flex items-center gap-2">
                    {getImportanceIcon(coverage.importance)}
                  </div>
                </div>
                
                <div className="mb-3">
                  <p className="text-2xl font-bold text-accent">{coverage.amount}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {getImportanceLabel(coverage.importance)}
                  </p>
                </div>
                
                <p className="text-sm text-muted-foreground">
                  {coverage.description}
                </p>
              </InteractiveCard>
            ))}
          </div>

          <div className="mt-8 p-6 bg-accent/10 border border-accent/30 rounded-lg">
            <p className="text-sm text-center">
              <strong>Preisspanne:</strong> {practiceType.priceRange}€ pro Monat • 
              <strong className="ml-2">Typische Praxisgröße:</strong> {practiceType.employeeRange} Mitarbeiter
            </p>
          </div>
        </div>
      </ScrollAnimatedSection>
    </section>
  );
};

export default RecommendedCoverage;
